import { useEffect, useRef, useState, useCallback } from 'react';
import getScrollParent from './getScrollParent';

interface Options {
  /** Pull distance (px) required to trigger a refresh. */
  threshold?: number;
  /** Max visual pull distance (px). */
  max?: number;
  /** Disable the gesture (e.g. when not on the relevant tab). */
  enabled?: boolean;
}

/**
 * Standard "pull down to refresh" for a touch list. Attach the returned
 * `anchorRef` to an element inside the scrollable area; the hook finds the
 * nearest scroll container and tracks the pull. `onRefresh` runs once the user
 * releases past the threshold. Returns the current `distance` (for a spinner /
 * content offset) and whether a refresh is in flight.
 */
export const usePullToRefresh = (
  onRefresh: () => Promise<void> | void,
  { threshold = 70, max = 110, enabled = true }: Options = {}
) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startY = useRef<number | null>(null);
  const distanceRef = useRef(0);
  const refreshingRef = useRef(false);

  const setDist = useCallback((d: number) => {
    distanceRef.current = d;
    setDistance(d);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const scrollEl = getScrollParent(anchorRef.current);
    if (!scrollEl) return;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      startY.current = scrollEl.scrollTop <= 0 ? e.touches[0].clientY : null;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current === null || refreshingRef.current) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0 && scrollEl.scrollTop <= 0) {
        e.preventDefault();
        // Resistance: pull moves at half speed and caps out.
        setDist(Math.min(delta * 0.5, max));
      } else {
        setDist(0);
      }
    };

    const onTouchEnd = async () => {
      if (startY.current === null) return;
      startY.current = null;
      if (distanceRef.current >= threshold) {
        refreshingRef.current = true;
        setRefreshing(true);
        setDist(threshold);
        try {
          await onRefresh();
        } finally {
          refreshingRef.current = false;
          setRefreshing(false);
          setDist(0);
        }
      } else {
        setDist(0);
      }
    };

    scrollEl.addEventListener('touchstart', onTouchStart, { passive: true });
    scrollEl.addEventListener('touchmove', onTouchMove, { passive: false });
    scrollEl.addEventListener('touchend', onTouchEnd);
    scrollEl.addEventListener('touchcancel', onTouchEnd);

    return () => {
      scrollEl.removeEventListener('touchstart', onTouchStart);
      scrollEl.removeEventListener('touchmove', onTouchMove);
      scrollEl.removeEventListener('touchend', onTouchEnd);
      scrollEl.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [onRefresh, threshold, max, enabled, setDist]);

  return { anchorRef, distance, refreshing };
};

export default usePullToRefresh;
